/**
 * 视图态与坐标换算。
 *
 * 铁律：**屏幕 ↔ 文档坐标的唯一入口就在这里**，业务代码禁止自己乘 zoom 系数。
 * 缩放/平移施加在承载 <svg> 的 HTML 容器上（CSS transform），
 * getScreenCTM() 会把该变换算进去 —— 所以文档 DOM 完全不动，导出天然干净。
 *
 * 本文件里「读真实布局」的函数（getScreenCTM/getBBox）在 jsdom 下返回 null，
 * 纯函数部分（clamp/viewTransform/fitView）可 100% 单测。
 */
import { IDENTITY, apply, fromCTM, invert, mul, type Mat, type Pt, type Rect } from './matrix';

export interface ViewState {
  zoom: number;
  panX: number;
  panY: number;
}

export interface Size {
  width: number;
  height: number;
}

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 10;

export const clampZoom = (z: number): number =>
  Number.isFinite(z) ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z)) : 1;

const px = (n: number): number => (Number.isFinite(n) ? Math.round(n * 100) / 100 : 0);

export const viewTransform = (v: ViewState): string =>
  `translate(${px(v.panX)}px, ${px(v.panY)}px) scale(${clampZoom(v.zoom)})`;

/**
 * 把内容框**居中**放进容器（给定缩放）：pan 让内容中心与容器中心重合。
 * 缩放后比容器大时 pan 为负 —— 这是对的：居中意味着两边各溢出同样多。
 */
export function centerView(container: Size, content: Rect, zoom = 1): ViewState {
  const z = clampZoom(zoom);
  const w = content.w > 0 ? content.w : 1;
  const h = content.h > 0 ? content.h : 1;
  return {
    zoom: z,
    panX: (container.width - w * z) / 2 - content.x * z,
    panY: (container.height - h * z) / 2 - content.y * z,
  };
}

/** 适应视图：给定容器尺寸与内容框，算出等比铺满（带留白）并居中的视图态 */
export function fitView(container: Size, content: Rect, padding = 24): ViewState {
  const availW = Math.max(1, container.width - padding * 2);
  const availH = Math.max(1, container.height - padding * 2);
  const w = content.w > 0 ? content.w : 1;
  const h = content.h > 0 ? content.h : 1;
  return centerView(container, content, Math.min(availW / w, availH / h));
}

const numbers = (v: string | null): number[] =>
  (v ?? '')
    .split(/[\s,]+/)
    .filter(Boolean)
    .map(Number);

/**
 * 文档尺寸：优先活的 viewBox，其次解析 viewBox 属性，最后退回 width/height。
 * 为什么不用 svg.viewBox.baseVal 一条路走到底：jsdom 不实现 SVGAnimatedRect，
 * 而属性解析在两个环境里行为一致（也让本函数可单测）。
 */
export const viewBoxRect = (svg: SVGSVGElement): Rect | null => {
  const live = svg.viewBox?.baseVal;
  if (live && live.width > 0 && live.height > 0) {
    return { x: live.x, y: live.y, w: live.width, h: live.height };
  }
  const vb = numbers(svg.getAttribute('viewBox'));
  if (vb.length === 4) {
    const [x, y, w, h] = vb as [number, number, number, number];
    if ([x, y, w, h].every(Number.isFinite) && w > 0 && h > 0) return { x, y, w, h };
  }
  const width = Number(svg.getAttribute('width'));
  const height = Number(svg.getAttribute('height'));
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { x: 0, y: 0, w: width, h: height };
  }
  return null;
};

/** 元素坐标系 → 根用户坐标（生产路径用 getCTM；无矩阵环境返回 null） */
export function matrixToRoot(el: Element, svg: SVGSVGElement): Mat | null {
  const svgCtm = svg.getScreenCTM?.();
  const elCtm = (el as SVGGraphicsElement).getScreenCTM?.();
  if (!svgCtm || !elCtm) return null;
  const inv = invert(fromCTM(svgCtm));
  return inv ? mul(inv, fromCTM(elCtm)) : null;
}

/** 屏幕 → 根用户坐标（文档坐标系） */
export function screenToRootMat(svg: SVGSVGElement): Mat | null {
  const ctm = svg.getScreenCTM?.();
  return ctm ? invert(fromCTM(ctm)) : null;
}

export function screenToRoot(svg: SVGSVGElement, p: Pt): Pt | null {
  const m = screenToRootMat(svg);
  return m ? apply(m, p) : null;
}

/** 位移向量从屏幕换算到根用户坐标（拖动移动用；父级有旋转/斜切也自动正确） */
export function screenDeltaToRoot(svg: SVGSVGElement, dx: number, dy: number): Pt {
  const m = screenToRootMat(svg);
  if (!m) return { x: dx, y: dy };
  return { x: m[0] * dx + m[2] * dy, y: m[1] * dx + m[3] * dy };
}

/** 元素在**根用户坐标系**里的轴对齐包围盒（getBBox() 是变换前坐标，必须再乘祖先链 CTM） */
export function bboxInRoot(el: SVGGraphicsElement, svg: SVGSVGElement): Rect | null {
  let local: DOMRect;
  try {
    local = el.getBBox();
  } catch {
    return null; // jsdom / 未渲染元素：没有布局信息就老实返回 null
  }
  const svgCtm = svg.getScreenCTM?.();
  const elCtm = el.getScreenCTM?.();
  if (!svgCtm || !elCtm) return null;
  const inv = invert(fromCTM(svgCtm));
  if (!inv) return null;
  const m = mul(inv, fromCTM(elCtm));

  const corners: Pt[] = [
    apply(m, { x: local.x, y: local.y }),
    apply(m, { x: local.x + local.width, y: local.y }),
    apply(m, { x: local.x, y: local.y + local.height }),
    apply(m, { x: local.x + local.width, y: local.y + local.height }),
  ];
  if (corners.some((c) => !Number.isFinite(c.x) || !Number.isFinite(c.y))) return null;
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** 多元素联合包围盒（选中框 / 对齐 / 适应选中都用它） */
export function bboxOfMany(els: Iterable<Element>, svg: SVGSVGElement): Rect | null {
  const boxes: Rect[] = [];
  for (const el of els) {
    const box = bboxInRoot(el as SVGGraphicsElement, svg);
    if (box) boxes.push(box);
  }
  if (boxes.length === 0) return null;
  const x = Math.min(...boxes.map((b) => b.x));
  const y = Math.min(...boxes.map((b) => b.y));
  const x2 = Math.max(...boxes.map((b) => b.x + b.w));
  const y2 = Math.max(...boxes.map((b) => b.y + b.h));
  return { x, y, w: x2 - x, h: y2 - y };
}

/** 两个轴对齐矩形是否相交（框选用；几何相交而非像素命中） */
export const rectsIntersect = (a: Rect, b: Rect): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export const IDENTITY_MAT: Mat = IDENTITY;
